import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PrimaryButton from '../atoms/PrimaryButton'
import SecondaryButton from '../atoms/SecondaryButton'
import UnsavedChangesModal from './UnsavedChangesModal'

interface FormFooterProps {
  cancelLabel: string
  onCancel: () => void
  submitLabel: string
  submitIcon?: React.ReactNode
  submitDisabled?: boolean
  submitType?: 'button' | 'submit'
  onSubmit?: () => void
  contentClassName?: string
  isDirty?: boolean
}

const FormFooter = ({
  cancelLabel,
  onCancel,
  submitLabel,
  submitIcon,
  submitDisabled = false,
  submitType = 'submit',
  onSubmit,
  contentClassName = '',
  isDirty = false
}: FormFooterProps): JSX.Element => {
  const { t } = useTranslation()
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false)
  const backPending = useRef(false)
  const skipGuard = useRef(false)

  useEffect(() => {
    if (!isDirty) {
      return () => {
        backPending.current = false
      }
    }

    window.history.pushState(window.history.state, '', window.location.href)

    const onPopState = (): void => {
      if (skipGuard.current) {
        return
      }
      window.history.pushState(window.history.state, '', window.location.href)
      backPending.current = true
      setIsLeaveModalOpen(true)
    }

    window.addEventListener('popstate', onPopState, true)

    return () => {
      window.removeEventListener('popstate', onPopState, true)
    }
  }, [isDirty])

  const handleCancel = (): void => {
    if (isDirty) {
      backPending.current = false
      setIsLeaveModalOpen(true)
      return
    }
    onCancel()
  }

  const confirmLeave = (): void => {
    setIsLeaveModalOpen(false)
    if (backPending.current) {
      backPending.current = false
      skipGuard.current = true
      window.history.go(-2)
      return
    }
    onCancel()
  }

  return (
    <>
      <div className="self-stretch py-3 bg-backgrounds-bg-field-component border-t border-borders-border-base flex flex-col justify-center items-center">
        <div className={`w-full px-4 inline-flex justify-between items-center ${contentClassName}`}>
          <SecondaryButton onClick={handleCancel}>{cancelLabel}</SecondaryButton>
          <PrimaryButton type={submitType} onClick={onSubmit} disabled={submitDisabled} icon={submitIcon}>
            {submitLabel}
          </PrimaryButton>
        </div>
      </div>
      <UnsavedChangesModal
        isOpen={isLeaveModalOpen}
        title={t('unsaved_changes.title')}
        body={t('unsaved_changes.body')}
        leaveLabel={t('unsaved_changes.leave')}
        stayLabel={t('unsaved_changes.stay')}
        onLeave={confirmLeave}
        onStay={() => {
          setIsLeaveModalOpen(false)
        }}
      />
    </>
  )
}

export default FormFooter
